import React, { useMemo, useState } from 'react';
import { Row, Col, Input, Select, DatePicker, Typography, Divider, Spin, Tag, message, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import type { QaqcPipeRfiForms } from '../../../../../types';
import StandardTable from '../../../../../components/common/StandardTable';
import ActionButtons from '../../../../../components/common/ActionButtons';
import { useAuth } from '../../../../../components/common/security/AuthContext';
import { useTablePagination } from '../../../../../components/common/useTablePagination';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { useGetQaqcPipeRfiForms } from '../../../../../hooks/genericHooks';
import { GoMoveToEnd } from 'react-icons/go';
import { CgAttachment } from 'react-icons/cg';
import Button from '../../../../../components/common/Button';
const RGoMoveToEnd: React.FC<any> = (props) =>
  React.createElement(GoMoveToEnd as unknown as React.ComponentType<any>, props) as React.ReactElement;
const RCgAttachment: React.FC<any> = (props) =>
  React.createElement(CgAttachment as unknown as React.ComponentType<any>, props) as React.ReactElement;

const { Title } = Typography;
const { RangePicker } = DatePicker;

const QaqcPipeRfiFormList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Filters
  const [subjectText, setSubjectText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [recipientsSearch, setRecipientsSearch] = useState('');
  const [appointeesSearch, setAppointeesSearch] = useState('');
  const [inviteesSearch, setInviteesSearch] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [inspTypes, setInspTypes] = useState<string[]>([]);

  // Pagination
  const { getPaginationProps } = useTablePagination(15);

  // Data via generic hook
  const { data, isLoading } = useGetQaqcPipeRfiForms();
  const forms: QaqcPipeRfiForms[] = data?.data || [];

  // If navigated here with a request to refresh, trigger event-based refetch and clear state
  React.useEffect(() => {
    const state: any = location.state;
    if (state?.refreshList) {
      try { window.dispatchEvent(new CustomEvent('qaqc_pipe_rfi_forms:changed')); } catch {}
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const filteredData = useMemo(() => {
    const subj = subjectText.trim().toLowerCase();
    const recSearch = recipientsSearch.trim().toLowerCase();
    const appSearch = appointeesSearch.trim().toLowerCase();
    const invSearch = inviteesSearch.trim().toLowerCase();
    const [start, end] = dateRange || [null, null];
    return (forms || []).filter(item => {
      const subjectOk = !subj || (item.rfi_subject || '').toLowerCase().includes(subj);
      const statusOk = !statusFilter.length || (item.rfi_status ? statusFilter.includes(item.rfi_status) : false);
      const inspOk = !inspTypes.length || (item.inspection_type ? inspTypes.includes(item.inspection_type) : false);
      const recipientsStr = Array.isArray(item.recipients) ? item.recipients.join('; ') : '';
      const recipientsOk = (() => {
        if (!recSearch) return true;
        const tokens = recSearch.split(/\s+/).filter(Boolean);
        const recStr = recipientsStr.toLowerCase();
        return tokens.every(t => recStr.includes(t));
      })();
      const appointeesOk = (() => {
        if (!appSearch) return true;
        const appStr = Array.isArray(item.appointees) ? item.appointees.join('; ').toLowerCase() : '';
        const tokens = appSearch.split(/\s+/).filter(Boolean);
        return tokens.every(t => appStr.includes(t));
      })();
      const inviteesOk = (() => {
        if (!invSearch) return true;
        const invStr = Array.isArray(item.invitees) ? item.invitees.join('; ').toLowerCase() : '';
        const tokens = invSearch.split(/\s+/).filter(Boolean);
        return tokens.every(t => invStr.includes(t));
      })();
      let dateOk = true;
      if (start || end) {
        const d = item.request_date ? dayjs(item.request_date) : null;
        if (!d) dateOk = false; else {
          if (start && d.isBefore(start, 'day')) dateOk = false;
          if (end && d.isAfter(end, 'day')) dateOk = false;
        }
      }
      return subjectOk && statusOk && recipientsOk && appointeesOk && inviteesOk && dateOk && inspOk;
    });
  }, [forms, subjectText, statusFilter, dateRange, recipientsSearch, appointeesSearch, inviteesSearch, inspTypes]);

  const columns: ColumnsType<QaqcPipeRfiForms> = useMemo(() => {
    const baseFirst: any[] = [
      { title: 'Form Ref', dataIndex: 'form_id', key: 'form_id', width: 100 },
      {
        title: '',
        key: 'has_files',
        width: 30,
        align: 'center' as const,
        render: (_: any, row: QaqcPipeRfiForms) => {
          const has = !!(row as any)?.upload_path && String((row as any).upload_path).trim() !== '';
          return has ? (
            <Tooltip title="Has attachments">
              <RCgAttachment size={16} color="#1677ff" />
            </Tooltip>
          ) : null;
        },
      },
    ];
    const baseRest: any[] = [
      { title: 'Doc Ref', dataIndex: 'work_permit_ref_num', key: 'work_permit_ref_num', width: 120 },
      { title: 'Description', dataIndex: 'work_permit_description', key: 'work_permit_description', ellipsis: true },
      { title: 'Issued By', dataIndex: 'issued_by', key: 'issued_by', width: 140 },
      { title: 'Due Date', dataIndex: 'request_date', key: 'request_date', width: 100, render: (v: string) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
      { title: 'Type', dataIndex: 'inspection_type', key: 'inspection_type', width: 60 },
      { title: 'Weld#', key: 'weld_count', width: 60, render: (_: any, row: QaqcPipeRfiForms) => Array.isArray(row.weldingdata_ids) ? row.weldingdata_ids.length : 0 },
      { title: 'Subject', dataIndex: 'rfi_subject', key: 'rfi_subject', ellipsis: true },
      { title: 'Recipients', dataIndex: 'recipients', key: 'recipients', width: 300, ellipsis: true, render: (arr?: string[]) => Array.isArray(arr) && arr.length ? arr.join('; ') : '' },
      { title: 'Appointees', dataIndex: 'appointees', key: 'appointees', width: 300, ellipsis: true, render: (arr?: string[]) => Array.isArray(arr) && arr.length ? arr.join('; ') : '' },
      { title: 'Invitees', dataIndex: 'invitees', key: 'invitees', width: 300, ellipsis: true, render: (arr?: string[]) => Array.isArray(arr) && arr.length ? arr.join('; ') : '' },
      { title: 'Status', dataIndex: 'rfi_status', key: 'rfi_status', width: 120, render: (s?: string) => {
        const color = s === 'active' ? 'green' : s === 'archived' ? 'default' : s === 'cancelled' ? 'red' : 'gold';
        return <Tag color={color}>{s || 'draft'}</Tag>;
      } },
      { title: 'Created At', dataIndex: 'created_at', key: 'created_at', width: 160, render: (v?: string) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
    ];
    const actionCol: any = {
      title: 'Action', key: 'action', width: 120, fixed: 'right',
      render: (_: any, row: QaqcPipeRfiForms) => {
        const currentPid = localStorage.getItem('personnel_id') || '';
        const recipientIds: string[] = Array.isArray((row as any).recipient_ids) ? (row as any).recipient_ids : [];
        const appointeeIds: string[] = Array.isArray((row as any).appointee_ids) ? (row as any).appointee_ids : [];
        const notCancelled = row.rfi_status !== 'cancelled';
        const canEdit = notCancelled && (currentPid ? recipientIds.includes(currentPid) : false);
        const isAppointee = currentPid ? appointeeIds.includes(currentPid) : false;
        return (
          <ActionButtons
            onView={() => navigate(`/qaqc/ndt/pipe_inspections/rfi/view/${row.id}`)}
            onEdit={canEdit ? () => navigate(`/qaqc/ndt/pipe_inspections/rfi/edit/${row.id}`) : undefined}
            showView={true}
            showEdit={true}
            showDelete={false}
            disableEdit={!canEdit}
            size="small"
            extra={
              <Tooltip title={isAppointee ? 'Proceed' : 'Only for appointees'}>
                <Button
                  action="add"
                  size="small"
                  disabled={!isAppointee}
                  icon={<RGoMoveToEnd size={16} color={isAppointee ? 'green' : '#bfbfbf'} />}
                  onClick={() => {
                    if (!isAppointee) return;
                    navigate(`/qaqc/ndt/pipe_inspections/inspection/create/${row.id}`, { state: { row } });
                  }}
                />
              </Tooltip>
            }
          />
        );
      }
    };
    return [...baseFirst, ...baseRest, actionCol];
  }, [navigate, user]);

  return (
    <>
      <Divider style={{ marginTop: 0 }} />
      <div style={{ padding: '0 24px' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>Pipe RFI Forms</Title>
          </Col>
          <Col>
            <Button action="add" type="primary" icon={<PlusOutlined />} onClick={() => navigate('/qaqc/ndt/pipe_inspections/rfi_request')}>
              New Pipe RFI
            </Button>
          </Col>
        </Row>

        {/* Filters */}
        <Row gutter={[4, 8]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Input
              placeholder="Search subject"
              prefix={<SearchOutlined />}
              value={subjectText}
              onChange={(e) => setSubjectText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Input
              placeholder="Search recipients"
              prefix={<SearchOutlined />}
              value={recipientsSearch}
              onChange={(e) => setRecipientsSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Input
              placeholder="Search appointees"
              prefix={<SearchOutlined />}
              value={appointeesSearch}
              onChange={(e) => setAppointeesSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Input
              placeholder="Search invitees"
              prefix={<SearchOutlined />}
              value={inviteesSearch}
              onChange={(e) => setInviteesSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={2}>
            <Select
              allowClear
              placeholder="Status"
              mode="multiple"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
              options={[
                { label: 'Draft', value: 'draft' },
                { label: 'Active', value: 'active' },
                { label: 'Archived', value: 'archived' },
                { label: 'Cancelled', value: 'cancelled' },
              ]}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={2}>
            <Select
              allowClear
              placeholder="Inspection"
              mode="multiple"
              value={inspTypes}
              onChange={(v) => setInspTypes(v)}
              options={[
                'BC','VC','PT','RT','MT','UT','PMI','PWHT'
              ].map(s => ({ label: s, value: s }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={24} md={8} lg={4}>
            <RangePicker
              style={{ width: '90%' }}
              value={dateRange as any}
              onChange={(vals) => setDateRange(vals as any)}
              placeholder={["Start date", "End date"]}
            />
          </Col>
        </Row>

        <Spin spinning={isLoading}>
          <StandardTable
            columns={columns as any}
            dataSource={filteredData}
            rowKey="id"
            loading={isLoading}
            pagination={getPaginationProps(filteredData.length)}
            scroll={{ x: 'max-content' }}
            bordered
            size="small"
          />
        </Spin>
      </div>
    </>
  );
};

export default QaqcPipeRfiFormList;
